import React, { useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
  title: string;
  message: React.ReactNode;
  type?: ToastType;
  duration?: number; // ms; 0 = sticky (no auto-dismiss, close button only)
  onClose: () => void;
}

/**
 * Toast component
 * Displays a single toast notification with title, message, type, and close button.
 * Handles auto-dismiss and fade/slide animation.
 */
const Toast: React.FC<ToastProps> = ({ title, message, type = 'info', duration = 8000, onClose }) => {
  useEffect(() => {
    if (duration === 0) return; // sticky: dismiss only via the close button
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  // Color and icon by type
  const typeStyles: Record<ToastType, React.CSSProperties> = {
    success: { borderLeft: '6px solid var(--brand)', background: '#e6f9ed' },
    error: { borderLeft: '6px solid var(--danger)', background: '#fdeaea' },
    info: { borderLeft: '6px solid #0ea5e9', background: '#e0f2fe' },
    warning: { borderLeft: '6px solid #f59e0b', background: '#fff7ed' },
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      tabIndex={0}
      className="toast-base"
      style={{
        minWidth: 280,
        maxWidth: 360,
        marginBottom: 16,
        borderRadius: 'var(--radius-md)',
        padding: '16px 20px 16px 16px',
        color: 'var(--text)',
        position: 'relative',
        fontFamily: 'inherit',
        ...typeStyles[type],
        animation: 'toast-in 0.3s cubic-bezier(.21,1.02,.73,1)'
      }}
    >
      <strong style={{ fontSize: 16, display: 'block', marginBottom: 4, paddingRight: 24 }}>{title}</strong>
      {/* Clamp + wrap defensively: an unexpectedly long message (e.g. a leaked
          server error) must never break the layout or overflow the toast. */}
      <div style={{ fontSize: 14, maxHeight: 200, overflowY: 'auto', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{message}</div>
      {/* Close button */}
      <button
        onClick={onClose}
        aria-label="Close notification"
        style={{
          position: 'absolute', top: 8, right: 8, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1, borderRadius: 6
        }}
      >
        ×
      </button>
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateX(40px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default Toast; 